import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  getNiceTickValues,
  useActiveTooltipCoordinate,
  useIsTooltipActive,
  usePlotArea,
} from 'recharts';
import { getEmptyRangeLabel } from './clientReportingUtils';
import {
  BENCHMARK_CHART_COLORS,
  resolveAccountChartColor,
} from './clientReportingBenchmarkConfig';
import { resolveBrokerKey } from './brokerBranding';
import { chartTheme, useColorMode } from '../../hooks/useColorMode';
import ClientReportingConnectPrompt from './ClientReportingConnectPrompt';
import { sparklineBounds } from './sparklineScale';

const CHART_MARGIN = { top: 12, right: 20, left: 10, bottom: 20 };
const CHART_MARGIN_COMPACT = { top: 12, right: 4, left: 0, bottom: 16 };
const Y_AXIS_WIDTH = 52;
const Y_AXIS_WIDTH_COMPACT = 40;
const CHART_HEIGHT = 420;
const Y_AXIS_HEADROOM = 1.12;
const Y_AXIS_BOTTOM_PADDING_RATIO = 0.1;
/** When peak-to-trough is below this fraction of balance, zoom Y to the band (matches sparklines). */
const TIGHT_Y_AXIS_RANGE_RATIO = 0.15;

/** Recharts' percentage width can lock to a narrow first paint — measure the shell. */
function useChartContainerWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const measure = () => {
      const next = Math.floor(node.getBoundingClientRect().width);
      if (next > 0) setWidth((prev) => (prev === next ? prev : next));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  return [ref, width];
}

function collectSeriesValues(data, seriesKeys) {
  const values = [];
  for (const point of data || []) {
    for (const key of seriesKeys) {
      const value = Number(point?.[key]);
      if (Number.isFinite(value)) values.push(value);
    }
  }
  return values;
}

function bottomPadding(dataMin, dataMax, isPercent) {
  const span = Math.max(dataMax - dataMin, 0);
  const minPad = isPercent ? 3 : 8;
  return Math.max(span * Y_AXIS_BOTTOM_PADDING_RATIO, Math.abs(dataMax) * 0.04, minPad);
}

function computeYAxisDomain(data, seriesKeys, isPercent = false) {
  let dataMin = Infinity;
  let dataMax = -Infinity;

  for (const point of data || []) {
    for (const key of seriesKeys) {
      const value = Number(point?.[key]);
      if (!Number.isFinite(value)) continue;
      if (value < dataMin) dataMin = value;
      if (value > dataMax) dataMax = value;
    }
  }

  if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) {
    return [0, 1];
  }

  const pad = bottomPadding(dataMin, dataMax, isPercent);

  if (dataMin === dataMax) {
    const anchor = dataMax;
    if (anchor === 0) return [-pad, Math.max(1, pad * 2)];
    const span = Math.abs(anchor) * Y_AXIS_HEADROOM;
    return [Math.min(0, anchor - span * 0.25) - pad, anchor * Y_AXIS_HEADROOM];
  }

  const span = dataMax - dataMin;
  const magnitude = Math.max(Math.abs(dataMax), Math.abs(dataMin), 1);

  // Large positive balances with tiny moves (typical NinjaTrader demo) were
  // drawn on a 0 → max*1.12 axis, so a ~$400 wiggle on ~$50k looked flat
  // while the portfolio sparkline — which zooms to the data band — showed curve.
  if (!isPercent && dataMin >= 0 && span < magnitude * TIGHT_Y_AXIS_RANGE_RATIO) {
    const { min, max } = sparklineBounds(collectSeriesValues(data, seriesKeys));
    const band = Math.max(max - min, 1);
    const chartPad = Math.max(band * 0.08, pad * 0.35);
    const yMin = Math.max(0, min - chartPad);
    const yMax = max + chartPad * Y_AXIS_HEADROOM;
    return [yMin, yMax];
  }

  const yMax = dataMax * Y_AXIS_HEADROOM;
  const yMin = dataMin >= 0
    ? -pad
    : dataMin - (dataMax - dataMin) * 0.1 - pad * 0.5;

  return [yMin, yMax];
}

function formatCurrencyValue(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `$${Math.round(n / 1000)}k`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatPercentValue(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}%`;
}

function formatTooltipValue(value, isPercent) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (isPercent) return formatPercentValue(n);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Math.abs(n) < 100 ? 2 : 0,
    maximumFractionDigits: Math.abs(n) < 100 ? 2 : 0,
  }).format(n);
}

function formatHeadlineDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${String(dateStr).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * @param {boolean} allowNegative  the series genuinely goes below zero.
 *
 * Negative labels are hidden by default because equity and profit charts pad
 * the axis below zero purely for headroom, and labelling that padding implies
 * data that is not there. Drawdown is the opposite case: EVERY value is
 * negative, so the blanket rule erased the entire axis and left the chart with
 * a single "0%" at the top and no scale at all.
 */
function chartTickFormatter(value, isPercent, allowNegative = false) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (n < 0 && !allowNegative) return '';
  if (n === 0) return isPercent ? '0%' : '$0';
  if (isPercent) return `${n.toFixed(n >= 100 || n <= -100 ? 0 : 1)}%`;
  return formatCurrencyValue(n);
}

function currencyYAxisTicks([yMin, yMax], allowNegative = false) {
  if (allowNegative) {
    // Zero is the line that says "made money" from "lost money", so it must be
    // labelled even when the automatic ticks step straight over it. Handing
    // back `undefined` here left the profit chart with a dotted baseline and
    // no number against it.
    const ticks = getNiceTickValues([yMin, yMax], 5, false);
    if (ticks.some((tick) => tick === 0)) return ticks;
    return [...ticks, 0].sort((a, b) => a - b);
  }
  if (yMin >= 0) return undefined;
  const ticks = getNiceTickValues([0, yMax], 5, false);
  return ticks.some((tick) => tick === 0) ? ticks : [0, ...ticks.filter((tick) => tick > 0)];
}

function resolveChartSeriesColor(name, dashedSeriesNames, chartLineColors, seriesAccounts) {
  const benchmarkColors = chartLineColors ?? BENCHMARK_CHART_COLORS;

  if (name && benchmarkColors[name]) {
    return benchmarkColors[name];
  }
  if (dashedSeriesNames?.has?.(name) && benchmarkColors[name]) {
    return benchmarkColors[name];
  }

  const account = seriesAccounts?.find((entry) => entry.name === name);
  const brokerKey = resolveBrokerKey(account?.name, account?.broker);
  return resolveAccountChartColor(chartLineColors, brokerKey);
}

function headlineSeriesColor(headlineSeries, dashedSeriesNames, chartLineColors, seriesAccounts) {
  if (!headlineSeries) return null;
  return resolveChartSeriesColor(headlineSeries, dashedSeriesNames, chartLineColors, seriesAccounts);
}

function CoinbaseClippedArea({
  headlineSeries,
  gradientId,
  baseValue,
}) {
  const plotArea = usePlotArea();
  const coordinate = useActiveTooltipCoordinate();
  const isActive = useIsTooltipActive();
  const areaClipId = useId().replace(/:/g, '');

  if (!plotArea) return null;

  const plotRight = plotArea.x + plotArea.width;
  const fillRight = isActive && coordinate?.x != null
    ? Math.min(Math.max(coordinate.x, plotArea.x), plotRight)
    : plotRight;
  const fillWidth = Math.max(0, fillRight - plotArea.x);

  return (
    <>
      <defs>
        <clipPath id={areaClipId}>
          <rect
            x={plotArea.x}
            y={plotArea.y}
            width={fillWidth}
            height={plotArea.height}
          />
        </clipPath>
      </defs>
      <Area
        type="linear"
        dataKey={headlineSeries}
        stroke="none"
        fill={`url(#${gradientId})`}
        baseValue={baseValue}
        isAnimationActive={false}
        dot={false}
        activeDot={false}
        legendType="none"
        tooltipType="none"
        clipPath={`url(#${areaClipId})`}
      />
    </>
  );
}

function CoinbaseHoverLine() {
  const plotArea = usePlotArea();
  const coordinate = useActiveTooltipCoordinate();
  const isActive = useIsTooltipActive();
  const lineClipId = useId().replace(/:/g, '');

  if (!isActive || !plotArea || coordinate?.x == null) return null;

  const plotRight = plotArea.x + plotArea.width;
  const x = Math.min(Math.max(coordinate.x, plotArea.x), plotRight);

  return (
    <g className="vd-coinbase-hover-line" pointerEvents="none">
      <defs>
        <clipPath id={lineClipId}>
          <rect
            x={plotArea.x}
            y={plotArea.y}
            width={plotArea.width}
            height={plotArea.height}
          />
        </clipPath>
      </defs>
      <g clipPath={`url(#${lineClipId})`}>
        <line
          x1={x}
          y1={plotArea.y}
          x2={x}
          y2={plotArea.y + plotArea.height}
          stroke="var(--theme-chart-hover, rgba(255,255,255,0.32))"
          strokeWidth={1}
        />
      </g>
    </g>
  );
}

function preferTooltipEntry(existing, candidate) {
  if (!existing) return candidate;
  const existingScore = existing.color ? 2 : 0;
  const candidateScore = candidate.color ? 2 : 0;
  return candidateScore >= existingScore ? candidate : existing;
}

function dedupeTooltipPayload(payload) {
  const byKey = new Map();

  for (const entry of payload) {
    const key = String(entry.dataKey ?? entry.name ?? '');
    if (!key) continue;
    byKey.set(key, preferTooltipEntry(byKey.get(key), entry));
  }

  const byName = new Map();
  for (const entry of byKey.values()) {
    const name = String(entry.name ?? entry.dataKey ?? '');
    if (!name) continue;
    byName.set(name, preferTooltipEntry(byName.get(name), entry));
  }

  return [...byName.values()];
}

function ClientReportingTooltip({ active, payload, label, isPercent, dashedSeriesNames = null }) {
  if (!active || !payload?.length) return null;

  const formattedDate = formatHeadlineDate(label) || label;
  const sortedPayload = dedupeTooltipPayload(payload).sort((a, b) => {
    const aIsBenchmark = dashedSeriesNames?.has?.(a.dataKey) || dashedSeriesNames?.has?.(a.name);
    const bIsBenchmark = dashedSeriesNames?.has?.(b.dataKey) || dashedSeriesNames?.has?.(b.name);
    if (aIsBenchmark === bIsBenchmark) return 0;
    return aIsBenchmark ? 1 : -1;
  });

  return (
    <div className="vd-chart-tooltip">
      <div className="vd-chart-tooltip-date">{formattedDate}</div>
      {sortedPayload.map((entry) => {
        const isBenchmark = dashedSeriesNames?.has?.(entry.dataKey)
          || dashedSeriesNames?.has?.(entry.name);
        return (
          <div
            key={entry.dataKey}
            className={`vd-chart-tooltip-line${isBenchmark ? ' is-benchmark' : ''}`}
          >
            <span className="swatch" style={{ background: entry.color }} />
            <span>{entry.name || entry.dataKey}</span>
            <strong>{formatTooltipValue(entry.value, isPercent)}</strong>
          </div>
        );
      })}
    </div>
  );
}

function CoinbaseStyleTooltip({
  active,
  payload,
  label,
  headlineSeries,
  syncHeadline,
  isPercent,
  dashedSeriesNames,
}) {
  if (active && payload?.length && headlineSeries && label != null) {
    const entry = payload.find((item) => item.dataKey === headlineSeries) || payload[0];
    const value = entry?.value == null ? null : Number(entry.value);
    if (value != null && Number.isFinite(value)) {
      syncHeadline(label, value);
    }
  }

  return (
    <ClientReportingTooltip
      active={active}
      payload={payload}
      label={label}
      isPercent={isPercent}
      dashedSeriesNames={dashedSeriesNames}
    />
  );
}

function ChartSkeleton() {
  return (
    <div
      className="vd-chart-wrap vd-chart-loader"
      role="status"
      aria-busy="true"
      aria-label="Loading equity chart"
    >
      <div className="vd-chart-loader-stage" aria-hidden="true">
        <div className="vd-chart-loader-grid">
          <span /><span /><span /><span /><span />
        </div>
        <svg
          className="vd-chart-loader-svg"
          viewBox="0 0 480 140"
          preserveAspectRatio="none"
        >
          <path
            className="vd-chart-loader-path vd-chart-loader-path--bench"
            d="M0,118 C72,112 140,96 210,88 S340,72 480,58"
          />
          <path
            className="vd-chart-loader-path vd-chart-loader-path--scenario"
            d="M0,122 C64,118 120,88 190,82 S310,38 400,22 S450,14 480,10"
          />
        </svg>
        <div className="vd-chart-loader-shimmer" />
        <p className="vd-chart-loader-caption">
          Loading equity chart
          <span className="vd-chart-loader-dots">
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </span>
        </p>
      </div>
    </div>
  );
}

/**
 * Shade the gap between zero and the line for an all-negative series.
 *
 * A bare drawdown line reads as "a line that happens to be low". Filling the
 * space it has fallen through makes the depth of the hole the thing you see,
 * which is what the chart is for.
 */
function renderNegativeFill(activeSeries, headlineSeries, gradientId) {
  const name = headlineSeries || activeSeries[0];
  if (!name) return null;
  return (
    <Area
      key={`${name}__fill`}
      type="linear"
      dataKey={name}
      stroke="none"
      fill={`url(#${gradientId}-neg)`}
      isAnimationActive={false}
      activeDot={false}
      legendType="none"
      tooltipType="none"
      baseValue={0}
    />
  );
}

function renderSeriesLines(activeSeries, seriesAccounts, dashedSeriesNames, headlineSeries, primaryColor, chartLineColors) {
  return activeSeries.map((name) => {
    const isBenchmark = dashedSeriesNames?.has?.(name);
    const isPrimary = name === headlineSeries;
    const color = isPrimary && primaryColor
      ? primaryColor
      : resolveChartSeriesColor(name, dashedSeriesNames, chartLineColors, seriesAccounts);

    return (
      <Line
        key={name}
        type="linear"
        dataKey={name}
        name={name}
        stroke={color}
        strokeWidth={isBenchmark ? 1.75 : isPrimary ? 2.25 : 2}
        strokeDasharray={isBenchmark ? '7 5' : undefined}
        dot={false}
        isAnimationActive={false}
        activeDot={isPrimary ? {
          r: 5,
          fill: color,
          stroke: '#ffffff',
          strokeWidth: 2,
        } : { r: 4 }}
      />
    );
  });
}

export default function PerformanceChart({
  data,
  series,
  seriesAccounts = [],
  valueFormat = 'currency',
  dateRange = '1Y',
  isInitialLoad = false,
  isRefreshing = false,
  dashedSeriesNames = null,
  showHeadlineValue = false,
  /** 'green' tints the filled area; null keeps the account's series color. */
  areaTone = null,
  /** When set and there is no real data, renders demoData behind a connect CTA. */
  connectPrompt = null,
  chartLineColors = null,
  compact = false,
}) {
  const isPercent = valueFormat === 'percent';
  const colors = chartTheme(useColorMode());
  const [chartShellRef, chartWidth] = useChartContainerWidth();
  const demoMode = Boolean(
    connectPrompt
    && connectPrompt.demoData?.length
    && connectPrompt.demoSeries?.length,
  );
  const chartData = demoMode ? connectPrompt.demoData : data;
  const chartSeries = demoMode ? connectPrompt.demoSeries : series;

  // Does the data actually dip below zero, or is the space under the axis just
  // padding? Drawdown lives entirely below it; equity and profit usually do not.
  // Only shade when the series never rises above zero — a mixed series would
  // get a red wash over its winning stretches, which says the opposite of the
  // truth.
  const allNegative = useMemo(() => {
    const values = (chartData || []).flatMap((point) => (chartSeries || [])
      .map((name) => Number(point?.[name]))
      .filter((v) => Number.isFinite(v)));
    return values.length > 0 && values.every((v) => v <= 0) && values.some((v) => v < 0);
  }, [chartData, chartSeries]);

  const hasNegativeValues = useMemo(
    () => (chartData || []).some((point) => (chartSeries || []).some((name) => Number(point?.[name]) < 0)),
    [chartData, chartSeries],
  );
  const gradientId = useId().replace(/:/g, '');
  const valueRef = useRef(null);
  const dateRef = useRef(null);
  const headlineCache = useRef({ date: null, value: null });

  const activeSeries = useMemo(
    () => (chartSeries || []).filter((name) => chartData?.some((point) => point?.[name] != null)),
    [chartData, chartSeries],
  );

  const headlineSeries = useMemo(() => {
    if (!activeSeries.length) return null;
    const primary = activeSeries.find((name) => !dashedSeriesNames?.has?.(name));
    return primary || activeSeries[0];
  }, [activeSeries, dashedSeriesNames]);

  const yAxisDomain = useMemo(
    () => computeYAxisDomain(chartData, activeSeries, isPercent),
    [chartData, activeSeries, isPercent],
  );

  const yAxisTicks = useMemo(
    () => (isPercent ? undefined : currencyYAxisTicks(yAxisDomain, hasNegativeValues)),
    [yAxisDomain, isPercent, hasNegativeValues],
  );

  // Drawdown is measured FROM zero, so the zero line is the reference the whole
  // chart is read against — it was previously suppressed on percent charts.
  const showZeroBaseline = yAxisDomain[0] < 0 && (!isPercent || hasNegativeValues);

  const latestPoint = chartData?.length ? chartData[chartData.length - 1] : null;
  const primaryColor = headlineSeriesColor(
    headlineSeries,
    dashedSeriesNames,
    chartLineColors,
    seriesAccounts,
  );

  // The fill can differ from the line: the line identifies the account, the
  // fill says what the number means.
  const fillColor = areaTone === 'green' ? '#22c55e' : primaryColor;

  const syncHeadline = useCallback((date, value) => {
    const nextValue = Number(value);
    if (!date || !Number.isFinite(nextValue)) return;
    if (headlineCache.current.date === date && headlineCache.current.value === nextValue) return;

    headlineCache.current = { date, value: nextValue };
    if (valueRef.current) {
      valueRef.current.textContent = formatTooltipValue(nextValue, isPercent);
    }
    if (dateRef.current) {
      dateRef.current.textContent = formatHeadlineDate(date);
    }
  }, [isPercent]);

  const resetHeadline = useCallback(() => {
    if (!latestPoint || !headlineSeries) return;
    syncHeadline(latestPoint.date, latestPoint[headlineSeries]);
  }, [latestPoint, headlineSeries, syncHeadline]);

  useEffect(() => {
    resetHeadline();
  }, [resetHeadline]);

  const emptyLabel = getEmptyRangeLabel(dateRange);
  const hasChart = chartData?.length > 0 && activeSeries.length > 0;
  const coinbaseStyle = showHeadlineValue && headlineSeries && !isPercent;

  if (isInitialLoad || (isRefreshing && !hasChart)) {
    return <ChartSkeleton />;
  }

  if (!hasChart) {
    return (
      <div className="vd-chart-empty">
        <p>No data found within {emptyLabel}.</p>
      </div>
    );
  }

  // Every account on the chart is a fresh link whose history is still loading.
  const loadingAccounts = (seriesAccounts || []).filter((a) => a?.historyPending);
  if (!demoMode && loadingAccounts.length && loadingAccounts.length === (seriesAccounts || []).length && chartData.length <= 2) {
    const names = [...new Set(loadingAccounts.map((a) => a.broker).filter(Boolean))].join(', ') || 'your broker';
    return (
      <div className="vd-chart-empty vd-chart-loading" role="status" aria-live="polite">
        <svg className="vd-chart-loading-line" viewBox="0 0 400 120" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0,95 C40,90 60,70 100,74 C140,78 160,50 200,52 C240,54 260,30 300,36 C340,42 360,20 400,18" pathLength="1" />
        </svg>
        <strong>Pulling in your history from {names}…</strong>
        <p>Your daily account values are on their way. This usually takes a few minutes; the chart appears on its own.</p>
      </div>
    );
  }

  // A single day (e.g. a brokerage linked today with no balance history yet):
  // a lone dot isn't a chart, so show the value and say what's coming.
  if (!demoMode && chartData.length === 1) {
    const point = chartData[0];
    const key = headlineSeries || activeSeries[0];
    const value = Number(point?.[key]);
    return (
      <div className="vd-chart-empty vd-chart-single">
        {Number.isFinite(value) ? <strong>{formatTooltipValue(value, isPercent)}</strong> : null}
        <span>{formatHeadlineDate(point.date)}</span>
        <p>Only one day of history so far. The chart fills in as daily values come in.</p>
      </div>
    );
  }

  const chartMargin = compact ? CHART_MARGIN_COMPACT : CHART_MARGIN;
  const yAxisWidth = compact ? Y_AXIS_WIDTH_COMPACT : Y_AXIS_WIDTH;

  const chartProps = {
    data: chartData,
    margin: chartMargin,
    onMouseLeave: coinbaseStyle ? resetHeadline : undefined,
  };

  const axisChildren = (
    <>
      <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
      {showZeroBaseline && (
        <ReferenceLine y={0} stroke={colors.zero} strokeDasharray="3 3" />
      )}
      <XAxis
        dataKey="date"
        tick={{ fontSize: 11, fill: colors.tick }}
        minTickGap={compact ? 18 : 24}
        padding={compact ? { left: 0, right: 0 } : undefined}
        axisLine={false}
        tickLine={false}
      />
      <YAxis
        tick={{ fontSize: 11, fill: colors.tick }}
        tickFormatter={(v) => chartTickFormatter(v, isPercent, hasNegativeValues)}
        domain={yAxisDomain}
        ticks={yAxisTicks}
        allowDataOverflow={!coinbaseStyle}
        axisLine={false}
        tickLine={false}
        width={yAxisWidth}
      />
    </>
  );

  const tooltipChild = coinbaseStyle ? (
    <Tooltip
      trigger="axis"
      animationDuration={0}
      isAnimationActive={false}
      cursor={false}
      content={(props) => (
        <CoinbaseStyleTooltip
          {...props}
          headlineSeries={headlineSeries}
          syncHeadline={syncHeadline}
          isPercent={isPercent}
          dashedSeriesNames={dashedSeriesNames}
        />
      )}
    />
  ) : (
    <Tooltip
      content={(props) => (
        <ClientReportingTooltip
          {...props}
          isPercent={isPercent}
          dashedSeriesNames={dashedSeriesNames}
        />
      )}
    />
  );

  const responsiveWidth = chartWidth > 0 ? chartWidth : '100%';

  return (
    <div
      ref={chartShellRef}
      className={`vd-chart-wrap vd-chart-shell${compact ? ' is-compact' : ''}${isRefreshing ? ' is-refreshing' : ''}${coinbaseStyle ? ' has-headline coinbase-style' : ''}${demoMode ? ' is-demo-preview' : ''}`}
    >
      {demoMode ? (
        <div className="vd-cr-connect-overlay" aria-hidden="false">
          <ClientReportingConnectPrompt
            title={connectPrompt.title}
            message={connectPrompt.message}
            actionLabel={connectPrompt.actionLabel}
            onAction={connectPrompt.onAction}
          />
        </div>
      ) : null}
      {coinbaseStyle && (
        <div className="vd-chart-headline" aria-live="polite">
          <div className="vd-chart-headline-value" ref={valueRef}>
            {formatTooltipValue(latestPoint?.[headlineSeries], isPercent)}
          </div>
          <div className="vd-chart-headline-date" ref={dateRef}>
            {formatHeadlineDate(latestPoint?.date)}
          </div>
        </div>
      )}
      <ResponsiveContainer width={responsiveWidth} height={CHART_HEIGHT} minWidth={0} debounce={50}>
        {coinbaseStyle ? (
          <ComposedChart {...chartProps}>
            <defs>
              {/* Densest at the top, where the line is, fading downward — the
                  color belongs to the value, not to the empty space beneath
                  it. Same reasoning as the drawdown fill, mirrored. */}
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={fillColor} stopOpacity={0.38} />
                <stop offset="55%" stopColor={fillColor} stopOpacity={0.12} />
                <stop offset="100%" stopColor={fillColor} stopOpacity={0.01} />
              </linearGradient>
            </defs>
            {axisChildren}
            {tooltipChild}
            {activeSeries.length > 1 && <Legend wrapperStyle={{ paddingTop: 8 }} />}
            <CoinbaseClippedArea
              headlineSeries={headlineSeries}
              gradientId={gradientId}
              baseValue={yAxisDomain[0]}
            />
            {renderSeriesLines(activeSeries, seriesAccounts, dashedSeriesNames, headlineSeries, primaryColor, chartLineColors)}
            <CoinbaseHoverLine />
          </ComposedChart>
        ) : (
          /* ComposedChart, not LineChart: an all-negative series (drawdown) gets
             a shaded Area hanging from zero, and LineChart cannot host one. */
          <ComposedChart {...chartProps}>
            <defs>
              {/* Densest at the BOTTOM, where the drawdown line sits, fading up
                  toward the zero baseline. The intuition is depth: the color
                  should be heaviest where the account actually is, not at the
                  high-water mark it has fallen away from. */}
              <linearGradient id={`${gradientId}-neg`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ef4444" stopOpacity={0.03} />
                <stop offset="55%" stopColor="#ef4444" stopOpacity={0.16} />
                <stop offset="100%" stopColor="#ef4444" stopOpacity={0.42} />
              </linearGradient>
            </defs>
            {axisChildren}
            {tooltipChild}
            <Legend />
            {allNegative && renderNegativeFill(activeSeries, headlineSeries, gradientId)}
            {renderSeriesLines(activeSeries, seriesAccounts, dashedSeriesNames, headlineSeries, primaryColor, chartLineColors)}
          </ComposedChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
